/**
 * Plan refusals, in one place (docs/plans/ENTITLEMENTS_GATES_PR2A.md 3.6).
 *
 * When a workspace's package does not allow a write, the gateway and the
 * services answer `{ success: false, error: <CODE>, code: <CODE>, message,
 * access_state, counts }`. The server's `message` wins when present; this
 * map is the fallback, and says the same words: the gateway's parity test
 * (apps/vhire-gateway/tests/authz/entitlement-write-gate.test.ts) imports this
 * file and compares it with the gateway's copy for every code. No em dashes.
 *
 * Keep this file free of imports: the gateway's test loads it as is.
 */

export const ENTITLEMENT_REFUSAL_CODES = [
  'WORKSPACE_READ_ONLY',
  'PAYMENT_PENDING',
  'FEATURE_NOT_IN_PLAN',
  'SEAT_LIMIT_REACHED',
  'ACTIVE_JOB_LIMIT',
  'ALLOWANCE_EXHAUSTED',
  'PACKAGE_NOT_LIVE',
  'ENTITLEMENTS_UNAVAILABLE',
  'CAREER_SITE_PACKAGE_CONTROLLED',
] as const;

export type EntitlementRefusalCode = (typeof ENTITLEMENT_REFUSAL_CODES)[number];

export const ENTITLEMENT_REFUSAL_STATUS: Readonly<Record<EntitlementRefusalCode, number>> = {
  WORKSPACE_READ_ONLY: 403,
  PAYMENT_PENDING: 403,
  FEATURE_NOT_IN_PLAN: 402,
  SEAT_LIMIT_REACHED: 402,
  ACTIVE_JOB_LIMIT: 402,
  ALLOWANCE_EXHAUSTED: 402,
  PACKAGE_NOT_LIVE: 403,
  ENTITLEMENTS_UNAVAILABLE: 503,
  CAREER_SITE_PACKAGE_CONTROLLED: 409,
};

const CODES: ReadonlySet<string> = new Set(ENTITLEMENT_REFUSAL_CODES);

export function isEntitlementRefusalCode(value: unknown): value is EntitlementRefusalCode {
  return typeof value === 'string' && CODES.has(value);
}

export interface RefusalCopyVars {
  /** A sentence subject: "The interview room", "The careers site". */
  feature?: string;
  /** Plural units: "assessment credits", "interview rooms". */
  unit?: string;
  /** The plan's limit, for seats and active jobs. */
  n?: number;
}

function countOf(n: number | undefined, one: string, many: string): string | null {
  if (n === undefined || !Number.isFinite(n)) return null;
  return `${n} ${n === 1 ? one : many}`;
}

/** The words for a refusal code. */
export function entitlementRefusalCopy(code: EntitlementRefusalCode, vars: RefusalCopyVars = {}): string {
  switch (code) {
    case 'WORKSPACE_READ_ONLY':
      return 'This workspace is read only. You can view and export your data. Go to Billing to make changes again.';
    case 'PAYMENT_PENDING':
      return 'Your workspace is in set-up until your payment is confirmed. You can finish set-up now.';
    case 'FEATURE_NOT_IN_PLAN':
      return `${vars.feature?.trim() || 'This feature'} is not included in your plan. Talk to us to add it.`;
    case 'SEAT_LIMIT_REACHED': {
      const seats = countOf(vars.n, 'seat', 'seats');
      return seats
        ? `Your plan includes ${seats} and all are in use. Free a seat or add seats from Billing.`
        : 'All the seats in your plan are in use. Free a seat or add seats from Billing.';
    }
    case 'ACTIVE_JOB_LIMIT': {
      const jobs = countOf(vars.n, 'active job', 'active jobs');
      return jobs
        ? `Your plan includes ${jobs} and all are in use. Close a job or upgrade to publish another.`
        : 'All the active jobs in your plan are in use. Close a job or upgrade to publish another.';
    }
    case 'ALLOWANCE_EXHAUSTED':
      return `Your ${vars.unit?.trim() || 'units'} for this term are used up. Add more or upgrade from Billing.`;
    case 'PACKAGE_NOT_LIVE':
      return 'Your plan does not allow this right now. Check Billing for your plan\'s status.';
    case 'ENTITLEMENTS_UNAVAILABLE':
      return 'We could not check your plan just now. Please try again in a minute.';
    case 'CAREER_SITE_PACKAGE_CONTROLLED':
      return 'Your plan does not include a careers site, so it cannot be switched on here. Talk to us to add it.';
  }
}

/** What a page needs to show a refusal. */
export interface EntitlementRefusal {
  code: EntitlementRefusalCode;
  message: string;
  accessState: string | null;
  /** Numbers only: limits and usage. */
  counts: Record<string, number>;
}

const METER_UNITS: Readonly<Record<string, string>> = {
  assessment_credit: 'assessment credits',
  interview_room: 'interview rooms',
};

function finiteNumber(value: unknown): number | undefined {
  return typeof value === 'number' && Number.isFinite(value) ? value : undefined;
}

/**
 * A plan refusal read off a failed response body, or null when the body is
 * not one. The code may come as `code` or (the gateway's 3.6 shape) `error`.
 */
export function readEntitlementRefusal(body: unknown): EntitlementRefusal | null {
  if (!body || typeof body !== 'object' || Array.isArray(body)) return null;
  const b = body as Record<string, unknown>;
  const code = isEntitlementRefusalCode(b.code) ? b.code : isEntitlementRefusalCode(b.error) ? b.error : null;
  if (!code) return null;

  const counts: Record<string, number> = {};
  if (b.counts && typeof b.counts === 'object') {
    for (const [key, value] of Object.entries(b.counts as Record<string, unknown>)) {
      const n = finiteNumber(value);
      if (n !== undefined) counts[key] = n;
    }
  }
  const serverMessage = typeof b.message === 'string' ? b.message.trim() : '';
  const meter = typeof b.meter === 'string' ? b.meter : '';
  const message = serverMessage || entitlementRefusalCopy(code, {
    n: counts.seats ?? counts.limit ?? finiteNumber(b.limit),
    unit: Object.hasOwn(METER_UNITS, meter) ? METER_UNITS[meter] : undefined,
  });
  return {
    code,
    message,
    accessState: typeof b.access_state === 'string' ? b.access_state : null,
    counts,
  };
}
